import BaseService from './base.service';

const AUTH_SESSION_STARTED_AT_KEY = 'scio.auth.sessionStartedAt';
const AUTH_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

class AuthUserService extends BaseService {
  constructor() {
    super('/api/AuthUser');
    this._currentUser = null;
    this._isAuthenticated = false;
    this._sessionWindowExpired = false;
  }

  isAuthenticated() {
    return this._isAuthenticated;
  }

  getCurrentUser() {
    return this._currentUser;
  }

  getUserRole() {
    // global role from backend claim scio:global_role
    return this._currentUser?.globalRole || null;
  }

  getScioUserId() {
    // scio:user_id from backend claim
    return this._currentUser?.userId ?? null;
  }

  startSessionWindow() {
    const now = Date.now();
    let startedAt = now;

    try {
      const storedStartedAt = Number(localStorage.getItem(AUTH_SESSION_STARTED_AT_KEY));
      if (Number.isFinite(storedStartedAt) && storedStartedAt > 0) {
        startedAt = storedStartedAt;
      } else {
        localStorage.setItem(AUTH_SESSION_STARTED_AT_KEY, String(startedAt));
      }
    } catch {
      // Private/hardened browser storage can be unavailable. The server-side
      // 24-hour cookie expiry remains authoritative in that case.
    }

    return AUTH_SESSION_MAX_AGE_MS - Math.max(0, now - startedAt);
  }

  clearSessionWindow() {
    try {
      localStorage.removeItem(AUTH_SESSION_STARTED_AT_KEY);
    } catch {
      // Storage may be unavailable in hardened browser contexts.
    }
  }

  clearLocalSession() {
    this._currentUser = null;
    this._isAuthenticated = false;
    this.clearSessionWindow();
  }

  async initFromCookie() {
    // Do not immediately re-adopt a still-present server cookie after the
    // client-side 24-hour window has expired. That would make the login route
    // call initFromCookie(), rediscover the cookie, and redirect back to the
    // protected route in a loop. A fresh SSO navigation creates a new service
    // instance and is the explicit re-authentication boundary.
    if (this._sessionWindowExpired) return null;

    try {
      const me = await this.get('me');
      if (me?.isAuthenticated === true) {
        if (this.startSessionWindow() <= 0) {
          this._sessionWindowExpired = true;
          this.clearLocalSession();
          return null;
        }

        this._currentUser = {
          userId: typeof me.userId === 'string' ? parseInt(me.userId, 10) : me.userId ?? null,
          email: me.email ?? null,
          globalRole: me.globalRole ?? null
        };
        this._isAuthenticated = true;
        return this._currentUser;
      }

      this.clearLocalSession();
      return null;
    } catch {
      this.clearLocalSession();
      return null;
    }
  }

  async logout({ skipServer = false } = {}) {
    this.clearLocalSession();

    if (!skipServer) {
      try {
        await this.post('logout');
      } catch {
        // Local logout must still complete if the server session already expired.
      }
    }
  }

  /**
   * Starts the interactive Entra flow. Server handles cookies; SPA just navigates.
   */
  beginSso(redirectUrl) {
    const useLocalApiProxy = String(import.meta.env.VITE_USE_LOCAL_API ?? '').toLowerCase() === 'true';
    const baseApi = useLocalApiProxy ? '' : (import.meta.env.VITE_SCIO_API_URL || '');
    const redirect = encodeURIComponent(redirectUrl || `${window.location.origin}/#/programmes/`);
    window.location.href = `${baseApi}/api/AuthUser/sso-token?redirect=${redirect}&cb=${Date.now()}`;
  }
}

export default new AuthUserService();
